import { MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, deviceKey, type ActivationMfa, type ApiClient, type AppName, type InvitationInfo, type MfaSetup, type SignedIn } from '@despachador/api-client';
import { AUTH_ERRORS } from '@despachador/contracts/auth-errors';
import { PASSWORD_RULES } from '@despachador/domain';
import { dateTime, Notice, useNow } from '../index';
import { goToGoogle, saveActivationTrip, type ActivationTrip } from './google-return';
import { AuthCard, AuthenticatorSetup, AuthSpinner, authMessage, CodeInput, countdown, errorCode, NewPassword, RecoveryCodes, retryAfterSeconds, waitText, type AuthBrand } from './pieces';
import { StatusCard } from './StatusScreen';

/** Wait this long before offering another code (the API allows 3 an hour per account). */
const RESEND_AFTER_S = 60;

/** Problems that end the activation: each one is a screen with what happened and what to do. */
type Problem = 'NO_TOKEN' | 'INVITATION_INVALID' | 'INVITATION_EXPIRED' | 'INVITATION_USED' | 'APP_NOT_ALLOWED' | 'TENANT_SUSPENDED' | 'PHONE_REQUIRES_UPDATE' | 'ACTIVATION_EXPIRED';
const ENDS_ACTIVATION: readonly string[] = ['INVITATION_INVALID', 'INVITATION_EXPIRED', 'INVITATION_USED', 'APP_NOT_ALLOWED', 'TENANT_SUSPENDED', 'PHONE_REQUIRES_UPDATE'];

/** The account belongs to the other app: say which one and how to get the right link. */
const RIGHT_APP: Record<AppName, string> = {
  admin: 'Tu cuenta es de conductor: se activa en la app del conductor, no en el panel de operación. Pide al administrador de tu empresa que te reenvíe la invitación: el enlace nuevo abre la app del conductor.',
  driver: 'Tu cuenta es del personal de la central: se activa en el panel de operación, no en la app del conductor. Pide al administrador de tu empresa que te reenvíe la invitación: el enlace nuevo abre el panel.',
};

function problemText(p: Problem, app: AppName): { title: string; text: string; tone: 'info' | 'warn' | 'danger'; action: string } {
  switch (p) {
    case 'NO_TOKEN':
      return {
        title: 'Abre el enlace de tu invitación',
        text: 'Esta página se abre con el enlace o el código QR que te entregó el administrador de tu empresa. Por seguridad el enlace no queda guardado en el navegador: si recargaste la página, ábrelo de nuevo.',
        tone: 'info',
        action: 'Ir al ingreso',
      };
    case 'INVITATION_INVALID':
      return {
        title: 'Este enlace no es válido',
        text: `${AUTH_ERRORS.INVITATION_INVALID.user} Si te entregaron uno más reciente, usa ese: cada enlace nuevo deja sin efecto el anterior.`,
        tone: 'warn',
        action: 'Ir al ingreso',
      };
    case 'INVITATION_EXPIRED':
      return { title: 'Este enlace venció', text: AUTH_ERRORS.INVITATION_EXPIRED.user, tone: 'warn', action: 'Ir al ingreso' };
    case 'INVITATION_USED':
      return { title: 'Tu cuenta ya está activa', text: AUTH_ERRORS.INVITATION_USED.user, tone: 'info', action: 'Ingresar' };
    case 'APP_NOT_ALLOWED':
      return { title: 'Este enlace es de otra aplicación', text: RIGHT_APP[app], tone: 'warn', action: 'Ir al ingreso' };
    case 'TENANT_SUSPENDED':
      return { title: 'Acceso de la empresa suspendido', text: AUTH_ERRORS.TENANT_SUSPENDED.user, tone: 'danger', action: 'Ir al ingreso' };
    case 'ACTIVATION_EXPIRED':
      // after the trip to Google there is no link in memory to ask for another code: it is opened again
      return { title: 'La verificación venció', text: AUTH_ERRORS.ACTIVATION_EXPIRED.user, tone: 'warn', action: 'Ir al ingreso' };
    case 'PHONE_REQUIRES_UPDATE':
      return {
        title: 'Falta tu número de celular',
        text: 'El celular registrado en tu cuenta no puede recibir el código. Pide al administrador de tu empresa que lo corrija y te envíe una invitación nueva.',
        tone: 'warn',
        action: 'Ir al ingreso',
      };
  }
}

type Phase = 'loading' | 'intro' | 'code' | 'password' | 'mfa-setup' | 'mfa-verify' | 'codes';

export interface ActivationProps {
  api: ApiClient;
  app: AppName;
  /** Brand of this address, used until the invitation names the company. */
  brand: AuthBrand;
  /** Token of the invitation link, in memory only (see activation-token.ts). null = the page was opened without one. */
  token: string | null;
  /** The company has sign-in with Google switched on: the person may link a Google account here (optional). */
  googleSignIn?: boolean;
  /** Back from choosing a Google account (google-return.ts): the activation resumes at the password step. */
  trip?: ActivationTrip | null;
  platform: 'WEB' | 'PWA';
  large?: boolean;
  /** Called on the last tap (a user gesture). */
  onGesture?: () => void;
  /** The account is active and has a session: the caller establishes it (same answer as a sign-in). */
  onSignedIn: (answer: SignedIn) => void;
  /** Leave the activation for the sign-in screen. */
  onSignIn: () => void;
}

/**
 * Activation of an invited account (`/acceso/activar`, ADR-015 §5.2), without a session:
 * who the invitation is for → code sent to the invited WhatsApp → new password → second factor when
 * the account needs it (set up here, or verified if it kept one) → signed in. The invitation token,
 * the ticket and the password live in this component's memory until the step that needs them.
 * The API is the authority at every step; this only guides.
 */
export function Activation({ api, app, brand: publicBrand, token, googleSignIn, trip, platform, large, onGesture, onSignedIn, onSignIn }: ActivationProps) {
  const [phase, setPhase] = useState<Phase>(trip ? 'password' : 'loading');
  // after the trip to Google only the company's name is known (nothing else of the invitation was kept)
  const [info, setInfo] = useState<InvitationInfo | null>(trip ? { brandName: trip.brandName || publicBrand.brandName, fullName: '', phoneMasked: '', steps: [], expiresAt: '' } : null);
  const [problem, setProblem] = useState<Problem | null>(token || trip ? null : 'NO_TOKEN');
  /** The invitation could not be checked (network, server, too many requests): retry keeps the token. */
  const [unchecked, setUnchecked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState('');
  /** When another code may be requested (ms). */
  const [resendAt, setResendAt] = useState(0);
  const [ticket, setTicket] = useState<{ value: string; mfa: ActivationMfa } | null>(trip ? { value: trip.activationTicket, mfa: trip.mfa } : null);
  /** The Google account chosen to link when the activation completes (its ticket and email). */
  const [google, setGoogle] = useState<{ ticket: string; email: string | null } | null>(null);
  const [googleNotice, setGoogleNotice] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [factor, setFactor] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [done, setDone] = useState<SignedIn | null>(null);

  const now = useNow(1000);
  const waitS = Math.max(0, Math.floor((resendAt - now) / 1000));
  const btn = `btn primary block ${large ? 'lg' : ''}`;
  const brand: AuthBrand = info ? { brandName: info.brandName, logoUrl: publicBrand.brandName === info.brandName ? publicBrand.logoUrl : null } : publicBrand;

  /** Errors that end the activation, wherever they come from. True when handled. */
  const ended = (err: unknown): boolean => {
    const c = errorCode(err);
    if (!c || !ENDS_ACTIVATION.includes(c)) return false;
    setProblem(c as Problem);
    return true;
  };
  /** A 429 says how long to wait when the API told it. */
  const message = (err: unknown): string => {
    const s = retryAfterSeconds(err);
    return s ? `Demasiados intentos. Podrás intentarlo de nuevo en ${waitText(s)}.` : authMessage(err);
  };

  // ---- step 1: who is this invitation for (once per token: StrictMode runs effects twice)
  const inspected = useRef(false);
  const inspect = () => {
    if (!token) return;
    setUnchecked(null);
    setPhase('loading');
    api.activation.inspect(token).then(
      (r) => {
        setInfo(r);
        setPhase('intro');
      },
      (err) => {
        if (!ended(err)) setUnchecked(message(err));
      },
    );
  };
  useEffect(() => {
    if (inspected.current) return;
    inspected.current = true;
    if (trip) return void inspectGoogle(trip);
    inspect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- optional: the Google account chosen during the activation
  const withoutGoogle = (why: string) => {
    setGoogle(null);
    setGoogleNotice(`${why} Tu cuenta se activará sin Google; podrás vincularla después desde tu cuenta.`);
  };
  const inspectGoogle = (t: ActivationTrip) => {
    if (!t.googleTicket) return withoutGoogle('No se vinculó ninguna cuenta de Google.');
    const googleTicket = t.googleTicket;
    api.activation.googleInspect(t.activationTicket, googleTicket).then(
      (r) => setGoogle({ ticket: googleTicket, email: r.email }),
      (err) => {
        const c = errorCode(err);
        if (c === 'ACTIVATION_EXPIRED') return token ? expireTicket() : setProblem('ACTIVATION_EXPIRED');
        if (ended(err)) return;
        withoutGoogle(c === 'EXTERNAL_TICKET_EXPIRED' || c === 'EXTERNAL_ALREADY_LINKED' ? authMessage(err) : 'No se pudo comprobar la cuenta de Google.');
      },
    );
  };
  const linkGoogle = async () => {
    if (!ticket) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.activation.googleStart(ticket.value);
      // the navigation unloads the page: only the ticket travels, in sessionStorage, and is removed on return
      saveActivationTrip({ activationTicket: ticket.value, mfa: ticket.mfa, brandName: brand.brandName });
      goToGoogle(url);
    } catch (err) {
      setBusy(false);
      if (ended(err)) return;
      if (errorCode(err) === 'ACTIVATION_EXPIRED') return token ? expireTicket() : setProblem('ACTIVATION_EXPIRED');
      setError(message(err));
    }
  };

  // ---- step 2: the code to the invited WhatsApp
  const sendCode = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.activation.sendCode(token);
      setCode('');
      setResendAt(Date.now() + RESEND_AFTER_S * 1000);
      if (phase === 'code') setNotice('Te enviamos un código nuevo. El anterior ya no sirve.');
      setPhase('code');
    } catch (err) {
      if (ended(err)) return;
      const s = retryAfterSeconds(err);
      if (s) setResendAt(Date.now() + s * 1000);
      setError(s ? `Ya se pidieron varios códigos. Podrás pedir otro en ${waitText(s)}.` : authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- step 3: the code -> the ticket
  const verifyCode = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.activation.verifyCode(token, code);
      setTicket({ value: r.activationTicket, mfa: r.mfa });
      setCode('');
      setPhase('password');
    } catch (err) {
      setCode('');
      if (ended(err)) return;
      const c = errorCode(err);
      const left = c === 'OTP_INVALID' ? (err as ApiError).details : null;
      const attemptsLeft = (left as { attemptsLeft?: unknown } | null)?.attemptsLeft;
      if (c === 'OTP_INVALID' && typeof attemptsLeft === 'number')
        setError(`${AUTH_ERRORS.OTP_INVALID.user} ${attemptsLeft === 1 ? 'Te queda 1 intento con este código.' : `Te quedan ${attemptsLeft} intentos con este código.`}`);
      else if (c === 'OTP_EXPIRED' || c === 'OTP_TOO_MANY_ATTEMPTS') {
        // this code is dead: another one can be requested right away
        setResendAt(0);
        setError(authMessage(err));
      } else setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- step 4: password (+ second factor), one endpoint called until it answers the session
  const complete = async (extra: { code?: string; recoveryCode?: string } = {}) => {
    if (!ticket) return;
    setBusy(true);
    setError(null);
    setPasswordError(null);
    try {
      const r = await api.activation.complete({ activationTicket: ticket.value, password, ...extra, ...(google ? { googleTicket: google.ticket } : {}), deviceKey: deviceKey(), platform });
      if (r.status === 'MFA_SETUP_REQUIRED') {
        setSetup(r.setup);
        setPhase('mfa-setup');
      } else if (r.status === 'MFA_REQUIRED') setPhase('mfa-verify');
      else {
        setPassword('');
        setRepeat('');
        setTicket(null);
        // recovery codes are handed over once, before entering
        if (r.recoveryCodes?.length) {
          setDone(r);
          setPhase('codes');
        } else onSignedIn(r);
      }
    } catch (err) {
      setFactor('');
      if (ended(err)) return;
      const c = errorCode(err);
      if (c === 'WEAK_PASSWORD') {
        const why = Array.isArray((err as ApiError).details) ? ((err as ApiError).details as Array<{ message?: unknown }>)[0]?.message : null;
        setPasswordError(typeof why === 'string' && why ? why : AUTH_ERRORS.WEAK_PASSWORD.user);
        setPhase('password');
      } else if (c === 'ACTIVATION_EXPIRED') {
        if (token) expireTicket();
        else setProblem('ACTIVATION_EXPIRED');
      } else if (c === 'EXTERNAL_TICKET_EXPIRED' || c === 'EXTERNAL_ALREADY_LINKED') {
        // nothing changed: the same call without Google activates the account
        withoutGoogle(authMessage(err));
        setPhase('password');
      } else setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  /** The ticket died (15 min, or a newer code): back to asking for a code, the link is still in memory. */
  function expireTicket() {
    setTicket(null);
    setPassword('');
    setRepeat('');
    setSetup(null);
    setGoogle(null);
    setResendAt(0);
    setNotice('La verificación venció. Pide otro código para continuar.');
    setPhase('intro');
  }

  const submitPassword = () => {
    setPasswordError(null);
    setRepeatError(null);
    if ([...password].length < PASSWORD_RULES.minLength) return setPasswordError(`Debe tener al menos ${PASSWORD_RULES.minLength} caracteres.`);
    if (password !== repeat) return setRepeatError('Las dos contraseñas no coinciden.');
    onGesture?.();
    void complete();
  };

  const on = (fn: () => void) => (e: FormEvent) => {
    e.preventDefault();
    fn();
  };

  // ---------------------------------------------------------------- screens
  if (problem) {
    const t = problemText(problem, app);
    return (
      <StatusCard brand={brand} title={t.title} tone={t.tone} large={large} action={{ label: t.action, onClick: onSignIn }}>
        {t.text}
      </StatusCard>
    );
  }
  if (unchecked)
    return (
      <StatusCard brand={brand} title="No pudimos comprobar tu invitación" tone="warn" large={large} action={{ label: 'Reintentar', onClick: inspect }} secondary={{ label: 'Ir al ingreso', onClick: onSignIn }}>
        {unchecked} Tu enlace sigue sirviendo.
      </StatusCard>
    );
  if (phase === 'loading' || !info)
    return (
      <AuthSpinner label="Comprobando tu invitación" brand={brand} />
    );

  if (phase === 'intro')
    return (
      <AuthCard key="intro" brand={brand} title="Activa tu cuenta" error={error} onSubmit={on(() => void sendCode())}>
        {notice && (
          <Notice tone="warn" urgent>
            {notice}
          </Notice>
        )}
        <p style={{ margin: 0 }}>
          Hola, <b>{info.fullName}</b>. Para activar tu cuenta te enviaremos un código de 6 dígitos al WhatsApp de tu celular <b className="nowrap">{info.phoneMasked}</b>.
        </p>
        <ol className="steps small">
          <li>Recibe el código en tu WhatsApp y escríbelo aquí.</li>
          <li>Elige tu contraseña.</li>
        </ol>
        <button className={btn} disabled={busy || waitS > 0}>
          <MessageCircle size={18} aria-hidden /> {busy ? 'Enviando…' : 'Enviar código a mi WhatsApp'}
        </button>
        {waitS > 0 && (
          <p className="small muted" style={{ margin: 0 }} role="status">
            Podrás pedir un código en {waitS > 90 ? waitText(waitS) : countdown(waitS)}.
          </p>
        )}
        <p className="small muted" style={{ margin: 0 }}>
          ¿Ese no es tu número? Pide al administrador de tu empresa que lo corrija. Este enlace vence el {dateTime(info.expiresAt).replace(/[.]$/, '')}.
        </p>
      </AuthCard>
    );

  if (phase === 'code')
    return (
      <AuthCard key="code" brand={brand} title="Escribe el código" error={error} onSubmit={on(() => void verifyCode())}>
        <Notice tone="info">
          {notice ?? (
            <>
              Enviamos un código de 6 dígitos al WhatsApp de <b className="nowrap">{info.phoneMasked}</b>.
            </>
          )}{' '}
          Vence en 10 minutos.
        </Notice>
        <CodeInput value={code} onChange={setCode} autoFocus label="Código de WhatsApp" hint="Los 6 dígitos del mensaje que acabas de recibir." />
        <button className={btn} disabled={busy || code.length !== 6}>
          {busy ? 'Verificando…' : 'Verificar código'}
        </button>
        <button type="button" className="btn ghost sm" disabled={busy || waitS > 0} onClick={() => void sendCode()}>
          Enviar otro código
        </button>
        {waitS > 0 && (
          <p className="small muted" style={{ margin: 0 }} role="status">
            ¿No te llegó? Podrás pedir otro en {waitS > 90 ? waitText(waitS) : countdown(waitS)}.
          </p>
        )}
      </AuthCard>
    );

  if (phase === 'password')
    return (
      <AuthCard key="password" brand={brand} title="Elige tu contraseña" error={error} onSubmit={on(submitPassword)}>
        <Notice tone="ok">Celular verificado. Con esta contraseña y tu celular ingresarás de ahora en adelante.</Notice>
        {googleNotice && (
          <Notice tone="warn" urgent>
            {googleNotice}
          </Notice>
        )}
        {google && (
          <Notice tone="info" title="Cuenta de Google elegida">
            Al activar se vinculará <b className="nowrap">{google.email ?? 'tu cuenta de Google'}</b>: podrás ingresar con ella o con tu contraseña.{' '}
            <button type="button" className="btn ghost sm" onClick={() => setGoogle(null)}>
              No vincular Google
            </button>
          </Notice>
        )}
        <NewPassword
          value={password}
          onChange={(v) => {
            setPassword(v);
            setPasswordError(null);
          }}
          repeat={repeat}
          onRepeat={(v) => {
            setRepeat(v);
            setRepeatError(null);
          }}
          error={passwordError}
          repeatError={repeatError}
          autoFocus
        />
        <button className={btn} disabled={busy}>
          {busy ? 'Guardando…' : ticket?.mfa === 'NONE' ? 'Activar mi cuenta' : 'Continuar'}
        </button>
        {googleSignIn && !google && !trip && (
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <p className="small muted" style={{ margin: 0 }}>
              Opcional: vincula tu cuenta de Google para ingresar también con ella. La contraseña sigue siendo necesaria.
            </p>
            <button type="button" className="btn block" disabled={busy} onClick={() => void linkGoogle()}>
              Vincular mi cuenta de Google
            </button>
          </div>
        )}
      </AuthCard>
    );

  if (phase === 'mfa-setup')
    return (
      <AuthCard key="mfa-setup" brand={brand} title="Configura la verificación en 2 pasos" error={error} onSubmit={on(() => void complete({ code: factor }))}>
        <Notice tone="info">Tu cuenta necesita un segundo paso al ingresar: un código que cambia cada 30 segundos en tu teléfono.</Notice>
        {setup && <AuthenticatorSetup {...setup} />}
        <CodeInput value={factor} onChange={setFactor} />
        <button className={btn} disabled={busy || factor.length !== 6}>
          {busy ? 'Verificando…' : 'Activar e ingresar'}
        </button>
      </AuthCard>
    );

  if (phase === 'mfa-verify')
    return (
      <AuthCard key="mfa-verify" brand={brand} title="Verificación en 2 pasos" error={error} onSubmit={on(() => void complete(recovery ? { recoveryCode: factor.trim() } : { code: factor }))}>
        <Notice tone="info">Tu cuenta conserva la verificación en 2 pasos: confirma que eres tú con el código de tu app de autenticación.</Notice>
        <CodeInput value={factor} onChange={setFactor} recovery={recovery} autoFocus />
        <button className={btn} disabled={busy || !factor.trim()}>
          {busy ? 'Verificando…' : 'Verificar e ingresar'}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => {
            setRecovery(!recovery);
            setFactor('');
            setError(null);
          }}
        >
          {recovery ? 'Usar el código de la app' : '¿No tienes la app? Usa un código de recuperación'}
        </button>
        <p className="small muted" style={{ margin: 0 }}>
          ¿Perdiste la app y los códigos? Pide al administrador de tu empresa que restablezca tu verificación en 2 pasos.
        </p>
      </AuthCard>
    );

  // codes: the account is active; the recovery codes are shown once before entering
  return (
    <AuthCard key="codes" brand={brand} title="Cuenta activada" done>
      <RecoveryCodes codes={done?.recoveryCodes ?? []} doneLabel="Entrar" large={large} onDone={() => done && onSignedIn(done)} />
    </AuthCard>
  );
}
